import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Video,
  Briefcase,
  Send,
  CheckCircle2,
  Settings,
  HelpCircle,
  LogOut,
  Bell,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  CalendarCheck,
  BookOpen,
  Sun,
  Kanban,
  Users,
  FileText,
  Star,
  CheckCircle,
} from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import vexisLogo from "@assets/vexis-logo-2026.jpg";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export type AgentTab =
  | "today"
  | "overview"
  | "registrations"
  | "interview-review"
  | "approval"
  | "roles"
  | "matching"
  | "shortlists"
  | "hired"
  | "settings"
  | "pipeline"
  | "jobs"
  | "candidates"
  | "dossiers"
  | "employers"
  | "scoring"
  | "matches"
  | "invites"
  | "howto"
  | "guide";

interface NavSection {
  label: string;
  items: NavItem[];
}

interface NavItem {
  id: AgentTab | "settings-route";
  label: string;
  icon: React.ElementType;
  description?: string;
  isRoute?: boolean;
  route?: string;
}

interface AgentDashboardLayoutProps {
  children: React.ReactNode;
  activeTab: AgentTab;
  onTabChange: (tab: AgentTab) => void;
  agentName: string;
  agentRole: string;
  onLogout: () => void;
  badges?: {
    registrations?: number;
    interviewReview?: number;
    approval?: number;
    shortlists?: number;
    dossierReady?: number;
    rolesNeedingQuestions?: number;
  };
}

const navSections: NavSection[] = [
  {
    label: "Daily Workflow",
    items: [
      {
        id: "today",
        label: "Today",
        icon: Sun,
        description: "Your daily priorities and actions",
      },
      {
        id: "pipeline",
        label: "Assessment Pipeline",
        icon: Kanban,
        description: "Track every candidate through assessment",
      },
    ],
  },
  {
    label: "Setup",
    items: [
      {
        id: "roles",
        label: "Roles & Questions",
        icon: Briefcase,
        description: "Manage roles and configure interview questions",
      },
      {
        id: "candidates",
        label: "Candidates",
        icon: Users,
        description: "All candidates in your workspace",
      },
      {
        id: "invites",
        label: "Invites",
        icon: Send,
        description: "Track all sent interview links",
      },
    ],
  },
  {
    label: "Results",
    items: [
      {
        id: "dossiers",
        label: "Dossiers",
        icon: FileText,
        description: "Review and send candidate dossiers to clients",
      },
      {
        id: "shortlists",
        label: "Shortlists",
        icon: Star,
        description: "Curated candidate shortlists for client review",
      },
      {
        id: "hired",
        label: "Placed",
        icon: CheckCircle,
        description: "Successfully placed candidates",
      },
    ],
  },
  {
    label: "Support",
    items: [
      {
        id: "guide",
        label: "User Guide",
        icon: BookOpen,
        description: "Step-by-step platform guide",
      },
      {
        id: "settings",
        label: "Settings",
        icon: Settings,
        description: "Account and workspace settings",
      },
    ],
  },
];

export function AgentDashboardLayout({
  children,
  activeTab,
  onTabChange,
  agentName,
  agentRole,
  onLogout,
  badges = {},
}: AgentDashboardLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [, navigate] = useLocation();

  const getBadgeInfo = (id: string): { count: number | undefined; color: "red" | "amber" | "green" | "default" } => {
    switch (id) {
      case "pipeline":
        return { count: (badges.interviewReview || 0) > 0 ? badges.interviewReview : undefined, color: "red" };
      case "dossiers":
        return { count: (badges.dossierReady || 0) > 0 ? badges.dossierReady : undefined, color: "green" };
      case "roles":
        return { count: (badges.rolesNeedingQuestions || 0) > 0 ? badges.rolesNeedingQuestions : undefined, color: "amber" };
      case "shortlists":
        return { count: badges.shortlists, color: "default" };
      default:
        return { count: undefined, color: "default" };
    }
  };

  const renderNavItem = (item: NavItem, isActive: boolean) => {
    const Icon = item.icon;
    const { count: badgeCount, color: badgeColor } = getBadgeInfo(item.id);
    const isPipeline = item.id === "pipeline";

    const dotBg =
      badgeColor === "red"
        ? "bg-[#EF4444]"
        : badgeColor === "amber"
        ? "bg-[#F59E0B]"
        : badgeColor === "green"
        ? "bg-[#22C55E]"
        : "bg-primary";
    const pillBg = isActive
      ? "bg-primary-foreground/20 text-primary-foreground"
      : badgeColor === "red"
      ? "bg-[#EF4444] text-white"
      : badgeColor === "amber"
      ? "bg-[#F59E0B] text-white"
      : badgeColor === "green"
      ? "bg-[#22C55E] text-white"
      : "bg-primary text-primary-foreground";

    const handleClick = () => {
      if (item.id === "settings") {
        onTabChange("settings");
      } else {
        onTabChange(item.id as AgentTab);
      }
      setMobileMenuOpen(false);
    };

    return (
      <button
        key={item.id}
        id={item.id === "dossiers" ? "tour-dossier" : item.id === "settings" ? "tour-settings" : undefined}
        onClick={handleClick}
        data-testid={`nav-${item.id}`}
        title={sidebarCollapsed && !mobileMenuOpen ? item.description : undefined}
        className={`
          group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
          ${
            isActive
              ? "bg-primary text-primary-foreground shadow-md"
              : "text-muted-foreground hover:bg-muted hover:text-foreground hover:shadow-sm"
          }
          ${isPipeline && !isActive ? "border border-primary/30" : ""}
          ${sidebarCollapsed && !mobileMenuOpen ? "justify-center" : ""}
        `}
      >
        <Icon
          className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 group-hover:scale-110 ${
            isPipeline && !isActive ? "text-primary" : ""
          }`}
        />
        {(!sidebarCollapsed || mobileMenuOpen) && (
          <>
            <span
              className={`flex-1 text-left text-sm font-medium ${
                isPipeline && !isActive ? "text-primary" : ""
              }`}
            >
              {item.label}
            </span>
            {badgeCount !== undefined && badgeCount > 0 && (
              <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${pillBg}`}>
                {badgeCount}
              </span>
            )}
          </>
        )}
        {sidebarCollapsed && !mobileMenuOpen && badgeCount !== undefined && badgeCount > 0 && (
          <span className={`absolute top-1 right-2 w-2 h-2 rounded-full ${dotBg}`} />
        )}
      </button>
    );
  };

  const renderSections = () => (
    <div className="space-y-4">
      {navSections.map((section, idx) => (
        <div key={section.label}>
          {idx > 0 && <hr className="border-border mb-3" />}
          {(!sidebarCollapsed || mobileMenuOpen) && (
            <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 select-none">
              {section.label}
            </p>
          )}
          <div className="space-y-0.5">
            {section.items.map((item) =>
              renderNavItem(item, activeTab === (item.id as AgentTab))
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div id="tour-welcome" className="min-h-screen bg-background flex" data-testid="agent-dashboard-layout">
      {/* Desktop Sidebar */}
      <aside
        className={`
          hidden lg:flex flex-col bg-card border-r border-border transition-all duration-300
          ${sidebarCollapsed ? "w-16" : "w-64"}
        `}
      >
        {/* Logo */}
        <div className={`p-4 border-b border-border ${sidebarCollapsed ? "px-2" : ""}`}>
          <div className="flex items-center justify-between">
            {!sidebarCollapsed && (
              <Link href="/" className="flex items-center gap-2">
                <img src={vexisLogo} alt="VEXIS" className="h-8 w-8 rounded-lg object-cover" />
                <span className="text-foreground font-bold text-xl">VEXIS</span>
              </Link>
            )}
            {sidebarCollapsed && (
              <Link href="/" className="w-full flex justify-center">
                <img src={vexisLogo} alt="VEXIS" className="h-8 w-8 rounded-lg object-cover" />
              </Link>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 overflow-y-auto" data-testid="sidebar-nav">
          {renderSections()}
        </nav>

        {/* Help button */}
        <div className="px-3 pb-2">
          <button
            onClick={() => window.open("/methodology", "_blank")}
            data-testid="nav-help"
            className={`
              group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
              text-muted-foreground hover:bg-muted hover:text-foreground hover:shadow-sm
              ${sidebarCollapsed ? "justify-center" : ""}
            `}
          >
            <HelpCircle className="w-5 h-5 flex-shrink-0 transition-transform duration-200 group-hover:scale-110" />
            {!sidebarCollapsed && <span className="flex-1 text-left text-sm font-medium">Help</span>}
          </button>
        </div>

        {/* Contact Support */}
        <a
          href="mailto:support@vexistalentagency.com"
          data-testid="nav-contact-support"
          style={{
            display: "block",
            padding: "12px 16px",
            color: "#64748b",
            fontSize: "13px",
            textDecoration: "none",
            borderTop: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          {!sidebarCollapsed && "💬 Contact Support"}
          {sidebarCollapsed && "💬"}
        </a>

        {/* Collapse Button */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="p-3 border-t border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          data-testid="toggle-sidebar"
        >
          {sidebarCollapsed ? (
            <ChevronRight className="w-5 h-5 mx-auto" />
          ) : (
            <div className="flex items-center gap-2">
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm">Collapse</span>
            </div>
          )}
        </button>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full w-64 bg-card border-r border-border z-50
          transform transition-transform duration-300 lg:hidden
          ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* Logo */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <img src={vexisLogo} alt="VEXIS" className="h-8 w-8 rounded-lg object-cover" />
            <span className="text-foreground font-bold text-xl">VEXIS</span>
          </Link>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3 overflow-y-auto h-[calc(100%-64px)]">
          {renderSections()}
          <div className="mt-4 border-t border-border pt-3">
            <button
              onClick={() => window.open("/methodology", "_blank")}
              className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground hover:shadow-sm transition-all duration-200"
            >
              <HelpCircle className="w-5 h-5 flex-shrink-0 transition-transform duration-200 group-hover:scale-110" />
              <span className="flex-1 text-left text-sm font-medium">Help</span>
            </button>
          </div>
        </nav>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Header */}
        <header className="bg-card border-b border-border px-4 lg:px-6 py-3" data-testid="dashboard-header">
          <div className="flex items-center justify-between">
            {/* Left: Logo (mobile only) + Mobile menu + Agent info */}
            <div className="flex items-center gap-4">
              <img
                src="/vexis-logo.png"
                alt="VEXIS"
                className="h-8 w-auto lg:hidden"
                data-testid="header-logo"
              />
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden text-muted-foreground hover:text-foreground"
                data-testid="mobile-menu-toggle"
              >
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-foreground font-semibold text-sm lg:text-base" data-testid="agent-name">
                  {agentName}
                </h1>
                <p className="text-muted-foreground text-xs lg:text-sm" data-testid="agent-role">
                  {agentRole}
                </p>
              </div>
            </div>

            {/* Right: Theme Toggle + Notifications + Profile */}
            <div className="flex items-center gap-2 lg:gap-4">
              <ThemeToggle />
              <button
                className="group relative p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-all duration-200"
                data-testid="notifications-button"
              >
                <Bell className="w-5 h-5 transition-transform duration-200 group-hover:scale-110" />
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                    data-testid="user-menu"
                  >
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-primary-foreground font-medium text-sm">
                        {String(agentName || "A")
                          .split(" ")
                          .filter((n) => n.length > 0)
                          .map((n) => n[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase() || "A"}
                      </span>
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {agentRole === "ADMIN" && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link
                          href="/mother"
                          className="flex items-center cursor-pointer"
                          data-testid="link-mother-dashboard"
                        >
                          <LayoutDashboard className="w-4 h-4 mr-2" />
                          Control Panel
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem
                    onClick={() => onTabChange("settings")}
                    className="cursor-pointer"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={onLogout}
                    className="text-destructive focus:text-destructive cursor-pointer"
                    data-testid="logout-button"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-background p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}

export default AgentDashboardLayout;
